import { computed, effectScope, h, onScopeDispose, reactive, ref, toValue, watch } from 'vue';
import type { Ref, VNode, VNodeChild } from 'vue';
import { useRoute } from 'vue-router';
import type {
  DataTableBaseColumn,
  DataTableExpandColumn,
  DataTableSelectionColumn,
  NDataTable,
  PaginationProps
} from 'naive-ui';
import * as _ from 'lodash-es';
import type { TableColumnGroup } from 'naive-ui/es/data-table/src/interface';
import { useBoolean, useLoading } from '@sa/hooks';
import type { FlatResponseData } from '@sa/axios';
import { useAppStore } from '@/store/modules/app';
import { buildTreeData, formatValueByDataType } from '@/utils/common';
import { $t } from '@/locales';
import type { RegisterParam } from '@/components/advanced/dynamic-element.vue';
import type { Api } from '@/typings/api';
import type { CommonType } from '@/typings/common';
import type { BasicTable } from '@/typings/basic-table';
import { useAuth } from '../../../hooks/business/auth';

export type BaseData = Record<string, unknown>;

// type ApiFn = (args: any) => Promise<unknown>;
export type ApiFn = (args: any) => Promise<FlatResponseData>;

/** 搜索表单字段 */
export interface SearchFieldSchema<T> {
  key: string;
  title: string;
  /** 默认跟随列显示，如果设置，则以此状态为主 */
  ifShow?: () => boolean;
  searchType: Api.Common.FilterType;
  helpMessage?: string;
  colSpan?: number | string;
  /** 默认值 */
  defaultValue?: any;
  /** 自动搜索 */
  autoSearch?: boolean;
  /** v-model字段名,默认为value */
  vModelField?: string;
  /** 是否禁止编辑，在添加和编辑时生效 */
  disabled?: boolean;
  /** 精确或模糊搜索切换 */
  showExactSwitch?: boolean;
  component: ComponentProps<CommonType.ComponentType> & {
    /** 组件类型 */
    // type: CommonType.ComponentType;
    /** 组件属性 */
    // props?: Record<string, any>;
    /** 组件插槽 */
    slots?:
      | Record<string, VNode | JSX.Element>
      | ((param: { data: T }) => Record<string, VNode | JSX.Element>)
      | Ref<Record<string, VNode | JSX.Element>>;
    register?: (param: RegisterParam<T>) => Record<string, any>;
  };
}
// 定义一个类型，接受组件类型并返回相应的 props 类型
type ComponentProps<Type extends CommonType.ComponentType> = {
  type: Type;
  // Record<string, any> | ((param: FieldCallbackParam<T>) => Record<string, any>) | Ref<Record<string, any>>
  props?: CommonType.ComponentPropsMap[Type];
};
/** 表格基础列配置 */
export type TableBaseColumn<T = any, CustomColumnKey = never> = Omit<DataTableBaseColumn<T>, 'key'> & {
  key: keyof T | CustomColumnKey;
  /** 列标题，title为function时取此值用于列配置 */
  titleText?: string;
  /** 是否显示 */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  ifShow?: (trigger: Function) => boolean;
  /** 需额外查询的字段 */
  requestRequiredColumns?: string[];
  /** 数据类型 */
  dataType: CommonType.DataType;
  /** 临时字段，不参与查询 */
  tempColumn?: boolean;
  /** 权限编码 */
  auth?: string | string[] | boolean;
  /** 枚举字典 */
  enumRecord?: Record<string, string | CommonType.TagOption<string | number>>;
  /** 是否合计 */
  sum?: boolean;
  /** 搜索配置 */
  search?: Omit<SearchFieldSchema<T>, 'key' | 'title'> & { key?: string; title?: string };
  /** 导出字段配置 */
  export?: Omit<Api.Common.ExportColumn, 'key' | 'label'> & { key?: string; label?: string };
};
/** 表格列配置 */
export type TableColumn<T = any, CustomColumnKey = never> =
  | (Omit<TableColumnGroup<T>, 'key'> & {
      key: keyof T | CustomColumnKey;
    })
  | TableBaseColumn<T, CustomColumnKey>
  | DataTableSelectionColumn<T>
  | DataTableExpandColumn<T>;

export type TransformedData<TableData extends BaseData = BaseData> = {
  data: TableData[];
  pageNum: number;
  pageSize: number;
  total: number;
};

/** transform api response to table data */
type Transformer<TableData extends BaseData = BaseData, Response = NonNullable<unknown>> = (
  response: Response
) => TransformedData<TableData>;

/** 基础列 */
export type BasicColumn = 'index' | 'selection' | 'operate';

/** table config */
export type TableConfig<TableData extends BaseData = BaseData, Fn extends ApiFn = ApiFn, CustomColumnKey = never> = {
  data?: TableData[] | Ref<TableData[] | undefined>;
  /** api function to get table data */
  apiFn?: Fn;
  /** 导出api function */
  exportFn?: Fn;
  /** api params */
  apiParams?: Omit<Parameters<Fn>[0], 'pageable'> & { pageable?: { page: number; size: number } };
  /** transform api response to table data */
  transformer?: Transformer<TableData, Awaited<ReturnType<Fn>>>;
  /** 是否是静态表格 */
  isStaticTable: Ref<boolean>;
  /** 是否分页 */
  isPage?: Ref<boolean>;
  /** pagination */
  pagination?: PaginationProps;
  /** 是否树形展示 */
  isTree?: Ref<boolean>;
  /** 父级字段,用于构建树形数据 */
  parentKey?: string;
  /** 子级字段,用于构建树形数据 */
  childrenKey?: string;
  /**
   * callback when pagination changed
   *
   * @param pagination
   */
  onPaginationChanged?: (pagination: PaginationProps) => void | Promise<void>;
  /**
   * whether to get data immediately
   *
   * @default true
   */
  immediate?: boolean;
  /** 基础操作列 */
  basicColumns?: BasicColumn[];
  /** 合并行，按照先后顺序进行合并 */
  mergeColumns?: string[];
  /** 多选列配置 */
  selectionColumnConfig?: BasicTable.SelectionColumnConfig<TableData>;
  /** columns */
  columns: Ref<TableColumn<TableData, CustomColumnKey>[]>;
  /** 导出字段配置 */
  exportColumns?: Api.Common.ExportColumn[];
  exportParams?: Api.Common.ExportParams;
};

/** filter columns */
export type FilteredColumn = {
  key: string;
  title: string;
  checked: boolean;
  requestRequiredColumns?: string[];
};
/** 合并数据行Map */
type MergeDataMap = Record<
  string,
  {
    // 起始位置
    startIndex: number;
    // 合并行数
    rowSpan: number;
    // 单元格值
    value: any;
  }
>;

export function useTable<TableData extends BaseData, Fn extends ApiFn, CustomColumnKey = never>(
  config: TableConfig<TableData, Fn, CustomColumnKey>
) {
  const scope = effectScope();
  const appStore = useAppStore();

  const dataTableRef = ref<InstanceType<typeof NDataTable> | null>(null);

  // 是否开启跨页选择
  const enableCrossPageChecked = ref<boolean>(false);
  const checkedRowKeys = ref<string[]>([]);

  watch(enableCrossPageChecked, val => {
    if (!val) {
      checkedRowKeys.value.length = 0;
    }
  });

  const { loading, startLoading, endLoading } = useLoading();
  const { bool: empty, setBool: setEmpty } = useBoolean();

  const {
    apiFn,
    exportFn,
    exportColumns,
    apiParams,
    isStaticTable,
    exportParams,
    transformer = response => {
      if (config.isPage?.value) {
        const { content = [], number, totalElements, size } = response.data || {};
        return {
          data: content,
          pageNum: (number || 0) + 1,
          pageSize: size || 20,
          total: totalElements || 0
        };
      }
      const { content = [] } = response.data || {};
      if (config.isTree?.value) {
        return {
          total: content.length,
          data: buildTreeData(content, {
            parentKey: config.parentKey || 'parent.id',
            childrenKey: config.childrenKey || 'children',
            keyField: 'id'
          })
        };
      }
      return {
        total: content.length,
        data: content
      };
    },
    onPaginationChanged = pg => {
      const { page = 1, pageSize = 20 } = pg;

      updateSearchParams({
        pageable: {
          page: page - 1,
          size: pageSize
        }
      });

      getData();
      // 清空选中
      if (!enableCrossPageChecked.value) {
        checkedRowKeys.value.length = 0;
      }
    },
    basicColumns = ['index'],
    immediate = true
  } = config;

  const searchParams: NonNullable<Parameters<Fn>[0]> = reactive({
    pageable: {
      page: 0,
      size: 20
    },
    ...apiParams
  });

  const data: Ref<TableData[]> = ref([]);
  /** 数据总数 */
  const totalNumber = ref(0);

  const pagination: false | PaginationProps =
    config.isPage?.value === false
      ? false
      : (reactive({
          page: 0,
          pageSize: 20,
          itemCount: 0,
          showSizePicker: true,
          pageSizes: [20, 50, 100],
          prefix: ({ itemCount }) => h('span', `共 ${itemCount} 条`),
          // Fix Naive Pagination's outdated API
          onUpdatePage: async (page: number) => {
            (pagination as PaginationProps).page = page;

            await onPaginationChanged?.(pagination as PaginationProps);
          },
          onUpdatePageSize: async (pageSize: number) => {
            (pagination as PaginationProps).pageSize = pageSize;
            (pagination as PaginationProps).page = 1;

            await onPaginationChanged?.(pagination as PaginationProps);
          },
          ...config.pagination
        }) as PaginationProps);

  const { columns, filteredColumns, searchFields, reloadColumns } = useTableColumn(
    config.columns,
    basicColumns,
    pagination,
    config.childrenKey ?? 'children',
    config.selectionColumnConfig ?? {},
    config.mergeColumns,
    config.isTree
  );

  function updatePagination(update: Partial<PaginationProps>) {
    Object.assign(pagination, update);
  }

  let lastRequestId = 0;
  async function getData() {
    // eslint-disable-next-line no-plusplus
    const requestId = ++lastRequestId;
    if (isStaticTable.value) {
      data.value = mergeDataColumns(toValue(config.data), config.mergeColumns) || [];
      // eslint-disable-next-line no-nested-ternary
      totalNumber.value = getTotalNumber(toValue(config.data), config.isTree?.value ?? false);
    } else {
      startLoading();
      try {
        const formattedParams = formatSearchParams(searchParams);

        const response = await apiFn?.(formattedParams);

        // 如果当前调用不是最新的调用，直接丢弃结果
        if (requestId !== lastRequestId) {
          return;
        }

        const { data: tableData, pageNum, pageSize, total } = transformer(response as Awaited<ReturnType<Fn>>);

        data.value = mergeDataColumns(tableData, config.mergeColumns);
        totalNumber.value = total;

        setEmpty(tableData.length === 0);
        updatePagination({ page: pageNum, pageSize, itemCount: total });
      } catch (error) {
        console.warn('Error fetching data:', error);
      } finally {
        if (requestId === lastRequestId) {
          // 只有最新的请求才会结束loading
          endLoading();
        }
      }
    }
  }
  /**
   * 合并数据行，将相同数据的行合并
   *
   * 按照顺序进行合并
   *
   * @param data
   * @param mergeColumns
   */
  function mergeDataColumns(tableData?: TableData[], mergeColumns?: string[]): TableData[] {
    if (!tableData || tableData.length === 0) {
      return [];
    }
    if (!mergeColumns || mergeColumns.length === 0) {
      return tableData;
    }
    const mergedData = _.cloneDeep(tableData);
    const mergeDataMap: MergeDataMap = {};
    mergeColumns.forEach(mergeColumn => {
      mergeDataMap[mergeColumn] = {
        startIndex: 0,
        rowSpan: 1,
        value: undefined
      };
    });
    let index = 0;
    for (const row of mergedData) {
      let continueMerge = true;
      for (const mergeColumn of mergeColumns) {
        const currentVal = _.get(row, mergeColumn);
        if (currentVal !== undefined && mergeDataMap[mergeColumn].value === currentVal) {
          // 如果当前行的值和上一个行的值相同，则行数加1
          mergeDataMap[mergeColumn].rowSpan += 1;
          _.set(row, `${mergeColumn.replaceAll('.', '_')}_rowSpan`, 1);
        } else {
          if (index > 0 && continueMerge) {
            setMergeData(mergeDataMap, mergedData, mergeColumns.slice(mergeColumns.indexOf(mergeColumn)));
            continueMerge = false;
          }
          mergeDataMap[mergeColumn].startIndex = index;
          mergeDataMap[mergeColumn].rowSpan = 1;
          mergeDataMap[mergeColumn].value = currentVal;
        }
      }
      index += 1;
    }
    setMergeData(mergeDataMap, mergedData, mergeColumns);
    return mergedData;
  }
  /**
   * @param rowData
   * @param mergeColumns
   * @param startMergeColumn
   * @returns
   */
  function setMergeData(mergeDataMap: MergeDataMap, mergedData: TableData[], mergeColumns: string[]) {
    for (const mergeColumn of mergeColumns) {
      const mergeMap = mergeDataMap[mergeColumn];
      _.set(mergedData[mergeMap.startIndex], `${mergeColumn.replaceAll('.', '_')}_rowSpan`, mergeMap.rowSpan);
      mergeMap.rowSpan = 1;
      mergeMap.value = undefined;
    }
  }
  /** 导出数据至Excel */
  async function exportData(): Promise<void> {
    if (exportFn) {
      console.log('exportData', exportParams);
      const exportColumnFiltered = getExportColumnsFromColumns(
        columns.value as TableColumn<TableData, CustomColumnKey>[]
      );
      const formattedParams = formatSearchParams(searchParams);
      if (!_.isEmpty(exportParams)) {
        formattedParams.exportParams = exportParams;
      }
      delete formattedParams.pageable;
      if (exportColumns && exportColumns.length > 0) {
        exportColumns.forEach((item, index) => {
          item.index = index;
        });
        formattedParams.exportColumns = exportColumns;
        await exportFn(formattedParams);
      } else if (exportColumnFiltered && exportColumnFiltered.length > 0) {
        exportColumnFiltered.forEach((item, index) => {
          item.index = index;
        });
        formattedParams.exportColumns = exportColumnFiltered;
        await exportFn(formattedParams);
      } else {
        console.warn('exportColumns is not defined');
        throw new Error('导出字段未定义');
      }
    } else {
      console.warn('exportFn is not defined');
      throw new Error('导出方法未定义');
    }
  }

  function getTotalNumber(tableData: TableData[] | undefined, isTree: boolean): number {
    if (tableData && tableData.length > 0) {
      if (isTree) {
        return tableData.reduce((acc, cur) => {
          return acc + getTotalNumber(cur.children as TableData[], isTree);
        }, tableData.length);
      }
      return tableData.length;
    }
    return 0;
  }

  function formatSearchParams(params: Record<string, unknown>) {
    const formattedParams: Record<string, unknown> = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formattedParams[key] = value;
      }
    });

    return formattedParams;
  }

  /**
   * update search params
   *
   * @param params
   */
  function updateSearchParams(params: Partial<Parameters<Fn>[0]>) {
    Object.assign(searchParams, params);
  }

  /** reset search params */
  function resetSearchParams() {
    Object.assign(searchParams, apiParams);
  }

  /** 从columns中获取exportColumns */
  function getExportColumnsFromColumns(tableColumns: TableColumn<TableData, CustomColumnKey>[]) {
    const exportColumnList: Api.Common.ExportColumn[] = tableColumns
      .filter(column => {
        return (column as TableBaseColumn).export;
      })
      .map(originalColumn => {
        const column = originalColumn as TableBaseColumn;
        return {
          key: column.key,
          label: column.title,
          ...column.export
        } as Api.Common.ExportColumn;
      });
    return exportColumnList;
  }

  if (immediate) {
    getData();
  }

  scope.run(() => {
    watch(
      () => appStore.locale,
      () => {
        reloadColumns();
      }
    );
  });

  onScopeDispose(() => {
    scope.stop();
  });

  return {
    dataTableRef,
    loading,
    empty,
    data,
    totalNumber,
    columns,
    filteredColumns,
    searchFields,
    reloadColumns,
    pagination,
    updatePagination,
    enableCrossPageChecked,
    checkedRowKeys,
    getData,
    exportData,
    searchParams,
    startLoading,
    endLoading,
    updateSearchParams,
    resetSearchParams
  };
}

/** 通用列，不需要权限过滤 */
const GeneralColumns = ['operate', 'index', 'selection'];

// eslint-disable-next-line max-params
function useTableColumn<TableData extends BaseData, CustomColumnKey = never>(
  originalColumns: Ref<TableColumn<TableData, CustomColumnKey>[]>,
  basicColumns: BasicColumn[],
  pagination: false | PaginationProps,
  childrenKey: string,
  selectionColumnConfig: BasicTable.SelectionColumnConfig<TableData>,
  mergeColumns?: string[],
  isTree?: Ref<boolean>
) {
  const SELECTION_KEY = '__selection__';

  const { hasAuth } = useAuth();
  const route = useRoute();

  const allColumns = ref([]) as Ref<TableColumn<TableData, CustomColumnKey>[]>;
  const filteredColumns: Ref<FilteredColumn[]> = ref([]);

  // 检索字段
  const searchFields: Ref<SearchFieldSchema<TableData>[]> = ref([]);

  watch(
    originalColumns,
    () => {
      reloadColumns();
    },
    {
      immediate: true
    }
  );

  const columns = computed(() => getColumns());

  function reloadColumns() {
    const { tableGroups } = route.meta;
    let groupColumns: string[] | null = null;
    if (tableGroups && (tableGroups as Api.Sys.MenuTableGroup[]).length > 0) {
      groupColumns = (tableGroups as Api.Sys.MenuTableGroup[]).reduce((acc, group) => {
        if (group.groupColumns) {
          return acc.concat(group.groupColumns.split(','));
        }
        return acc;
      }, [] as string[]);
    }
    // 获取所有列,并校验权限
    allColumns.value = originalColumns.value.filter(column => {
      // 1.过滤分组权限
      if (
        groupColumns &&
        'key' in column &&
        !GeneralColumns.includes(column.key as string) &&
        !groupColumns.includes(column.key as string)
      ) {
        return false;
      }
      // 2.过滤字段权限
      if ('auth' in column && column.auth) {
        if (typeof column.auth === 'boolean') {
          return column.auth;
        }
        return hasAuth(column.auth);
      }
      return true;
    });
    configColumnFormat();
    configColumnMerge();
    searchFields.value = getSearchFields(allColumns.value);
    if (basicColumns) {
      if (basicColumns.includes('index')) {
        allColumns.value.splice(0, 0, {
          key: 'index',
          title: $t('common.index'),
          dataType: 'number',
          fixed: 'left',
          render: (rowData, index): string => getIndex(rowData, index),
          align: 'center',
          width: 64
        });
      }
      if (basicColumns.includes('selection')) {
        allColumns.value.splice(0, 0, {
          type: 'selection',
          align: 'center',
          fixed: 'left',
          width: 48,
          ...selectionColumnConfig
        });
      }
    }
    filteredColumns.value = getFilteredColumns(allColumns.value);
  }
  /** 设置列的格式化 */
  function configColumnFormat() {
    for (const column of allColumns.value) {
      // 如果未定义render，则根据字段数据类型进行渲染
      if (!('render' in column) && 'key' in column && 'dataType' in column) {
        let render!: (rowData: TableData, rowIndex: number) => VNodeChild;
        const key = column.key as keyof TableData;
        const emptyValue = null;
        let columnVal: (rowData: TableData) => any = (rowData: TableData) => {
          return rowData[key];
        };
        // 如果字段包含【.】，则需要解构数据后再处理
        if (typeof key === 'string' && key.includes('.')) {
          columnVal = rowData => {
            let result;
            if (column.dataType === 'array') {
              const arrVal = rowData[key.split('.')[0]] as Array<any>;
              if (arrVal && arrVal.length > 0) {
                result = arrVal.map((item: any) => _.get(item, key.substring(key.indexOf('.') + 1), emptyValue));
              }
            } else {
              result = _.get(rowData, key, emptyValue) as string;
            }
            return result;
          };
        }
        if (column.enumRecord) {
          const enumRecord = column.enumRecord;
          render = rowData => {
            const val = columnVal(rowData);
            if (val !== null && val !== undefined) {
              const enumVal = enumRecord[val];
              // eslint-disable-next-line no-nested-ternary
              return enumVal !== null && enumVal !== undefined
                ? typeof enumVal !== 'string'
                  ? enumVal.label
                  : enumVal
                : val;
            }
            return emptyValue;
          };
        } else {
          render = rowData => {
            const value = formatValueByDataType(columnVal(rowData), column.dataType);
            // eslint-disable-next-line no-warning-comments
            // TODO 根据数据类型格式化值显示方式
            return value;
          };
        }
        if (render) {
          (column as TableBaseColumn<TableData, CustomColumnKey>).render = render;
        }
      }
    }
  }
  /** 配置行合并 */
  function configColumnMerge() {
    if (mergeColumns && mergeColumns.length > 0) {
      allColumns.value.forEach(column => {
        if ('key' in column && mergeColumns.includes(column.key as string)) {
          _.set(column, 'rowSpan', (rowData: TableData) => {
            const rowSpan = rowData[`${(column.key as string).replaceAll('.', '_')}_rowSpan`];
            if (rowSpan) {
              return rowSpan;
            }
            return 1;
          });
        }
      });
    }
  }

  function getFilteredColumns(aColumns: TableColumn<TableData, CustomColumnKey>[]) {
    const cols: FilteredColumn[] = [];

    aColumns.forEach(column => {
      const showColumn = ref(true);
      if ('ifShow' in column && column.ifShow) {
        showColumn.value = toValue(column.ifShow(reloadColumns));
      }
      if (showColumn.value) {
        if (column.type === undefined) {
          cols.push({
            key: column.key as string,
            title:
              // eslint-disable-next-line no-nested-ternary
              typeof column.title === 'function'
                ? 'titleText' in column && column.titleText
                  ? column.titleText
                  : '--'
                : (column.title as string),
            checked: true,
            requestRequiredColumns: 'requestRequiredColumns' in column ? column.requestRequiredColumns : undefined
          });
        }

        if (column.type === 'selection') {
          cols.push({
            key: SELECTION_KEY,
            title: $t('common.check'),
            checked: true
          });
        }
      }
    });

    return cols;
  }

  function getColumns() {
    const cols = filteredColumns.value
      .filter(column => column.checked)
      .map(column => {
        if (column.key === SELECTION_KEY) {
          return allColumns.value.find(col => col.type === 'selection');
        }
        return allColumns.value.find(col => (col as DataTableBaseColumn).key === column.key);
      });
    return cols as TableColumn<TableData, CustomColumnKey>[];
  }

  /**
   * 获取搜索字段
   *
   * @param aColumns 所有列
   */
  function getSearchFields(aColumns: TableColumn<TableData, CustomColumnKey>[]): SearchFieldSchema<TableData>[] {
    return aColumns
      .filter(column => {
        if ('search' in column && column.search !== undefined && typeof column.search === 'object') {
          const ifShow = ref(true);
          if (column.search.ifShow) {
            ifShow.value = column.search.ifShow();
          } else if ('ifShow' in column && column.ifShow) {
            ifShow.value = toValue(column.ifShow(reloadColumns));
          }
          return ifShow.value;
        }
        return false;
      })
      .map(column => {
        const search = 'search' in column && column.search !== undefined ? column.search : {};
        let key = 'key' in search && search.key !== undefined ? search.key : undefined;
        if (key === undefined) {
          key = 'key' in column ? column.key : '';
        }
        let title = 'title' in search && search.title !== undefined ? search.title : undefined;
        if (title === undefined) {
          title = 'title' in column ? column.title : '';
        }
        return {
          key,
          title,
          ...('search' in column ? (column.search as Omit<SearchFieldSchema<TableData>, 'key' | 'title'>) : {})
        } as SearchFieldSchema<TableData>;
      });
  }
  function getIndex(rowData: TableData, index: number): string {
    if (isTree?.value) {
      if (childrenKey in rowData && Array.isArray(rowData[childrenKey])) {
        (rowData[childrenKey] as any[]).forEach((item: any, i: number) => {
          item.index = `${index + 1}-${String(i + 1)}`;
        });
      } else {
        return (rowData.index ?? index + 1) as string;
      }
    }
    if (pagination === false) {
      return String(index + 1);
    }
    const { page = 0, pageSize = 20 } = pagination;

    return String(((page === 0 ? 1 : page) - 1) * pageSize + index + 1);
  }

  return {
    columns,
    reloadColumns,
    filteredColumns,
    searchFields
  };
}
